import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    EventEmitter,
    ViewEncapsulation,
} from '@angular/core';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { findClosestElement, isDefined, isEqual } from '@fi-shared/utils';
import { IEmoji } from '@fi-shared/fi-emoji-button/fi-emoji';
import { Observable, Subject, Subscription } from 'rxjs';
import { EmojiService } from '@ctrl/ngx-emoji-mart/ngx-emoji';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiEmojiService } from '@fi-shared/fi-emoji-button/fi-emoji.service';
import { takeUntil } from 'rxjs/operators';
import { isNullOrUndefined } from 'util';

@Component({
    selector: 'fi-emoji-button',
    template: `
        <button class="emoji-button" mat-icon-button (mousedown)="toggleEmojiPicker($event)">
            <mat-icon><span class="fas fa-smile"></span></mat-icon>
        </button>
    `,
    styleUrls: ['./fi-emoji-button.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-emoji-button' },
    standalone: false,
})
export class FiEmojiButtonComponent implements OnInit, OnDestroy {
    @Input() messageInput: ElementRef<HTMLElement>;
    @Input() recalculateEmojisContainerPosition: Observable<any> = new Observable();
    @Input() contentChangedObservable: Observable<void> = new Observable<void>();
    @Output() onContentChange = new EventEmitter();
    private emojiInsertionSubscription: Subscription = new Subscription();
    private range: Range;
    private isEmojiMartShown: boolean = false;

    constructor(
        private browserEventsService: FiBrowserEventsService,
        private elementRef: ElementRef,
        private emojiService: EmojiService,
        private renderer: Renderer2,
        private detectDeviceService: DetectDevicesService,
        private fiEmojiService: FiEmojiService,
    ) {}

    public ngOnInit(): void {
        this.browserEventsService
            .getDocumentEventSubject$('mousedown')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(($event: MouseEvent) => {
                if (!($event.target instanceof Element)) return;
                if (findClosestElement($event.target as HTMLElement, this.elementRef.nativeElement)) return;
                if (
                    this.fiEmojiService.emojiOverlayElementRef &&
                    findClosestElement(
                        $event.target as HTMLElement,
                        this.fiEmojiService.emojiOverlayElementRef.nativeElement,
                    )
                )
                    return;
                if (this.isEmojiMartShown) this.toggleEmojiPicker($event);
            });
        this.contentChangedObservable.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => this.setRange());
    }

    protected toggleEmojiPicker($event: MouseEvent): void {
        $event.preventDefault();
        this.setRange();
        this.isEmojiMartShown = !this.isEmojiMartShown;

        this.fiEmojiService.setEmojiOverlayVisibility({
            isEmojiOverlayShown: this.isEmojiMartShown,
            triggerElement: this.elementRef,
        });

        this.isEmojiMartShown ? this.subscribeToEmojiInsertion() : this.unsubscribeFromEmojiInsertion();
    }

    private setRange(): void {
        const selection = document.getSelection();
        this.range = isNullOrUndefined(selection.anchorNode) ? undefined : selection.getRangeAt(0);
    }

    private subscribeToEmojiInsertion(): void {
        this.emojiInsertionSubscription = this.fiEmojiService.onEmojiInsertion$().subscribe((emoji: IEmoji) => {
            const emojiElement: HTMLImageElement = this.fiEmojiService.getEmojiImgElement(emoji.sheet, emoji);
            if (!isDefined(this.range)) {
                this.messageInput.nativeElement.appendChild(emojiElement);
            } else {
                const rangeAncestorContainer: Node = this.range.commonAncestorContainer;
                const isFocusOnOutside =
                    rangeAncestorContainer.nodeType === Node.TEXT_NODE
                        ? !rangeAncestorContainer.parentElement.matches('[contenteditable]')
                        : !findClosestElement(rangeAncestorContainer as HTMLElement, this.messageInput.nativeElement);

                if (isFocusOnOutside) {
                    this.messageInput.nativeElement.appendChild(emojiElement);
                } else {
                    this.range.deleteContents();
                    this.range.insertNode(emojiElement);
                    this.range.setStartAfter(emojiElement);
                    const sel = document.getSelection();
                    sel.removeAllRanges();
                    sel.addRange(this.range);
                }
            }
            this.onContentChange.emit();
        });
    }

    private unsubscribeFromEmojiInsertion(): void {
        this.emojiInsertionSubscription.unsubscribe();
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
        this.unsubscribeFromEmojiInsertion();
    }
}
